import React from 'react';
import { useLang } from '../i18n/LanguageContext';

// Home page write-up (Tamil + English), as provided by the CM Secretary's
// office (Oct 2026). Text is official — keep it verbatim. The language
// toggle in the navbar picks which version is shown.
const CONTENT = {
  ta: {
    title: 'ஏரி இணையதளம்',
    subtitle: 'தன்னார்வலர்களின் பங்களிப்புடன் நீர்நிலைகளைப் புனரமைத்தல் மற்றும் தடையின்மைச் சான்று (NOC) வழங்குவதற்கான இணையதளம்',
    tagline: 'ஒரே தளம் • எளிய விண்ணப்பம் • விரைவான அனுமதி • வெளிப்படையான கண்காணிப்பு',
    paras: [
      'தமிழ்நாட்டில் பல்வேறு அரசுத் துறைகள் மற்றும் உள்ளாட்சி அமைப்புகளின் கட்டுப்பாட்டில் ஒரு இலட்சத்திற்கும் மேற்பட்ட ஏரிகள், குளங்கள், குட்டைகள் மற்றும் பிற பாரம்பரிய நீர்நிலைகள் உள்ளன.',
      'காலநிலை மாற்றம் மற்றும் அதிகரித்து வரும் நீர்த் தேவையைக் கருத்தில் கொண்டு, நீர்நிலைகளின் நீர் சேமிப்புத் திறனை அதிகரிக்கவும், நிலத்தடி நீர் செறிவூட்டலை மேம்படுத்தவும், காலநிலை மாற்றத்தின் தாக்கங்களை எதிர்கொள்ளும் திறனை வலுப்படுத்தவும், தமிழ்நாட்டின் நீர் வளப் பாதுகாப்பை உறுதி செய்யவும், நீர்நிலைகளைப் பாதுகாத்தல், புனரமைத்தல் மற்றும் புத்துயிரூட்டுதல் போன்ற பணிகளில் பொதுமக்களின் பங்களிப்பை உறுதி செய்வது மிகவும் அவசியமாகும்.',
      'தமிழ்நாடு முழுவதும் உள்ள நீர்நிலைகளின் புனரமைப்பு மற்றும் புத்துயிரூட்டும் பணிகளில் பொதுமக்கள், தன்னார்வத் தொண்டு நிறுவனங்கள் (NGOs), பெருநிறுவன சமூகப் பொறுப்பு (CSR) அமைப்புகள், கல்வி மற்றும் பிற நிறுவனங்கள் உள்ளிட்ட பல்வேறு தரப்பினரின் பங்கேற்பை ஊக்குவிக்கும் வகையில், ஏரி இணையதளம் ஒரு பொதுவான ஒருங்கிணைந்த இணையவழித் தளமாக உருவாக்கப்பட்டுள்ளது.',
      'ஏரி இணையதளத்தின் மூலம், ஆர்வமுள்ள அமைப்புகள் புனரமைப்பிற்கான நீர்நிலைகளைத் தேர்வு செய்யவும், புனரமைப்புத் திட்ட முன்மொழிவுகளை இணையவழியில் சமர்ப்பிக்கவும், சம்பந்தப்பட்ட அரசுத் துறைகளிடமிருந்து தேவையான அனுமதி / தடையின்மைச் சான்றுகளை (NOC) ஒரே தளத்தின் மூலம் பெறவும், விண்ணப்பத்தின் நிலையைக் கண்காணிக்கவும், புனரமைப்புப் பணிகளின் முன்னேற்றம் மற்றும் நிறைவு விவரங்களைப் பதிவு செய்யவும் இயலும்.',
    ],
    motto: 'நீர்நிலைகளைப் பாதுகாப்போம் • சமூகப் பங்கேற்பை ஊக்குவிப்போம் • நீர் வளப்பாதுகாப்பை உறுதி செய்வோம்',
  },
  en: {
    title: 'ERI WEBSITE',
    subtitle: 'Website for Restoration of Water Bodies with the Participation of Volunteers and Issuance of No Objection Certificates (NOC)',
    tagline: 'One Platform • Easy Application • Faster Approvals • Transparent Monitoring',
    paras: [
      'Tamil Nadu has more than one lakh water bodies, including Eris, kulams, kuttais and other traditional water bodies, managed by various Government Departments and Local Bodies.',
      'In the face of climate change and increasing water demand, to enhance water storage capacity, improve groundwater recharge, strengthen resilience to the impacts of climate change, and ensure the water security of Tamil Nadu, the effective participation of NGOs, the public, and volunteers, along with the Government, will go a long way in making the efforts sustainable.',
      'Through the ERI portal, an integrated online platform created to encourage the participation of citizens, NGOs, CSR organisations, institutions and other stakeholders in the restoration and rejuvenation of water bodies across the State.',
      'Through ERI portal, interested organisations can identify water bodies, submit restoration proposals, obtain the necessary permissions/NOCs from the concerned Government Departments, track their applications, and document the progress and completion of restoration works through a single platform.',
    ],
    motto: 'Protect Water Bodies • Promote Community Participation • Ensure Water Security',
  },
};

const IntroWriteup = () => {
  const { lang } = useLang();
  const data = CONTENT[lang] || CONTENT.en;
  return (
    <section className="py-10 bg-slate-100">
      <div className="container mx-auto px-6 lg:px-12 max-w-5xl">
        <div className="bg-white border border-slate-200 border-t-4 border-t-brand-600">
          <div className="px-6 lg:px-10 pt-8 text-center">
            <h2 className="text-3xl lg:text-4xl font-semibold text-brand-800">{data.title}</h2>
            <p className="mt-3 text-base lg:text-lg font-medium text-slate-700 leading-relaxed">{data.subtitle}</p>
            <p className="mt-4 inline-block bg-brand-50 border-l-4 border-brand-accent px-4 py-2 text-sm lg:text-base font-semibold text-brand-800">
              {data.tagline}
            </p>
          </div>
          <div className="px-6 lg:px-10 py-6 space-y-4 text-[15px] lg:text-base text-slate-700 leading-relaxed">
            {data.paras.map((p) => (
              <p key={p.slice(0, 24)}>{p}</p>
            ))}
          </div>
          <div className="bg-brand-800 px-6 py-3 text-sm lg:text-base font-semibold text-white text-center">
            {data.motto}
          </div>
        </div>
      </div>
    </section>
  );
};

export default IntroWriteup;
